import { z } from 'zod';

// Form field validation schemas
export const formFieldSchema = z.object({
  name: z.string().trim().min(1, "Field name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().max(20).regex(/^[\d\s+\-()]+$/, "Invalid phone number format").optional(),
  text: z.string().trim().max(1000, "Text must be less than 1000 characters"),
  textarea: z.string().trim().max(5000, "Text must be less than 5000 characters"),
  number: z.number().or(z.string().regex(/^\d+(\.\d+)?$/, "Must be a valid number")),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),
  checkbox: z.boolean(),
  radio: z.string().trim().max(100),
  select: z.string().trim().max(100),
});

// Submission data validation
export const submissionDataSchema = z.record(
  z.string().max(100), // field name
  z.union([
    z.string().trim().max(5000),
    z.number(),
    z.boolean(),
    z.array(z.string().max(100))
  ])
);

// WhatsApp phone number validation (E.164 format)
export const whatsappPhoneSchema = z.string()
  .trim()
  .regex(/^\+[1-9]\d{1,14}$/, "Invalid phone number. Must be in E.164 format (e.g., +2348012345678)")
  .min(8)
  .max(16);

// WhatsApp message validation
export const whatsappMessageSchema = z.string()
  .trim()
  .min(1, "Message cannot be empty")
  .max(4096, "WhatsApp message cannot exceed 4096 characters");

// Email validation
export const emailSchema = z.string()
  .trim()
  .email("Invalid email address")
  .max(255)
  .toLowerCase();

// Password validation with complexity requirements
export const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters long")
  .max(128, "Password is too long")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character");

// URL validation
export const urlSchema = z.string()
  .trim()
  .url("Invalid URL format")
  .max(2048);

// Sanitize and validate user input for external APIs
export function sanitizeForUrl(input: string): string {
  // Remove any potentially dangerous characters
  return encodeURIComponent(input.trim().slice(0, 1000));
}

// Validate form submission data
export function validateSubmissionData(data: any): { valid: boolean; sanitized?: any; errors?: string[] } {
  try {
    const result = submissionDataSchema.parse(data);
    return { valid: true, sanitized: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { 
        valid: false, 
        errors: error.errors.map(e => `${e.path.join('.')}: ${e.message}`)
      };
    }
    return { valid: false, errors: ['Invalid submission data'] };
  }
}

// Validate WhatsApp phone number
export function validateWhatsAppPhone(phone: string): { valid: boolean; error?: string } {
  try {
    whatsappPhoneSchema.parse(phone);
    return { valid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { valid: false, error: error.errors[0].message };
    }
    return { valid: false, error: 'Invalid phone number' };
  }
}

// Validate WhatsApp message
export function validateWhatsAppMessage(message: string): { valid: boolean; error?: string } {
  try {
    whatsappMessageSchema.parse(message);
    return { valid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { valid: false, error: error.errors[0].message };
    }
    return { valid: false, error: 'Invalid message' };
  }
}

// Validate password with complexity requirements
export function validatePassword(password: string): { valid: boolean; errors?: string[] } {
  try {
    passwordSchema.parse(password);
    return { valid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { 
        valid: false, 
        errors: error.errors.map(e => e.message)
      };
    }
    return { valid: false, errors: ['Invalid password'] };
  }
}
